/**
 * Mock posts data for development.
 * Covers all 4 post types: text, workout, meal, progress.
 * Timestamps are relative to now so data always looks fresh.
 */
import { subDays, subHours } from 'date-fns';
import { MOCK_USERS, MOCK_CURRENT_USER_ID } from './mock-users.data';

const now = new Date();

const u = (id) => MOCK_USERS.find((x) => x.id === id) || MOCK_USERS[0];

const makePost = (overrides) => ({
  id: overrides.id || `mock_post_${Date.now().toString(36)}`,
  type: 'text',
  text: '',
  imageUrl: null,
  authorId: overrides.authorId || MOCK_CURRENT_USER_ID,
  authorName: u(overrides.authorId || MOCK_CURRENT_USER_ID).displayName,
  authorUsername: u(overrides.authorId || MOCK_CURRENT_USER_ID).username,
  authorAvatarUrl: u(overrides.authorId || MOCK_CURRENT_USER_ID).avatarUrl,
  likesCount: 0,
  commentsCount: 0,
  edited: false,
  createdAt: new Date().toISOString(),
  updatedAt: null,
  workout: null,
  meal: null,
  progress: null,
  tags: [],
  mentions: [],
  _docSnapshot: null,
  ...overrides,
});

export const MOCK_POSTS = [
  // ── TEXT POSTS ──────────────────────────────────────────────────────────────
  makePost({
    id: 'mock_post_t01',
    text: 'Just hit a new #deadlift PR - 405lbs! Months of consistent training finally paying off. Remember: progress isn\'t always linear, but showing up every day is what counts. #prs #gains',
    tags: ['deadlift', 'prs', 'gains'],
    authorId: MOCK_CURRENT_USER_ID,
    likesCount: 47,
    commentsCount: 12,
    createdAt: subHours(now, 2).toISOString(),
  }),
  makePost({
    id: 'mock_post_t02',
    text: 'Morning run in the rain hits different. 6 miles before sunrise. Who else is part of the 5am club? #cardio #morningroutine @marcusj',
    tags: ['cardio', 'morningroutine'],
    mentions: ['mock_user_003'],
    authorId: 'mock_user_002',
    likesCount: 34,
    commentsCount: 8,
    createdAt: subHours(now, 6).toISOString(),
  }),
  makePost({
    id: 'mock_post_t03',
    text: 'Rest day reminder: #recovery IS training. Your muscles grow when you rest, not when you lift. Foam rolling, stretching, and a good meal today. #fitness',
    tags: ['recovery', 'fitness'],
    authorId: 'mock_user_008',
    likesCount: 22,
    commentsCount: 5,
    createdAt: subDays(now, 1).toISOString(),
  }),
  makePost({
    id: 'mock_post_t04',
    text: 'Question for the community: what\'s your go-to pre-workout meal? I usually do oatmeal with banana and peanut butter about 90 minutes before training. #nutrition #mealprep @sarahchen',
    tags: ['nutrition', 'mealprep'],
    mentions: ['mock_user_002'],
    authorId: 'mock_user_004',
    likesCount: 18,
    commentsCount: 14,
    createdAt: subDays(now, 2).toISOString(),
  }),
  makePost({
    id: 'mock_post_t05',
    text: 'Gym etiquette PSA: please re-rack your weights. It takes 10 seconds and makes everyone\'s day better. #gymlife #noexcuses',
    tags: ['gymlife', 'noexcuses'],
    authorId: 'mock_user_003',
    likesCount: 51,
    commentsCount: 9,
    createdAt: subDays(now, 3).toISOString(),
  }),
  makePost({
    id: 'mock_post_t06',
    text: 'Started incorporating zone 2 #cardio into my routine and my recovery between sets has improved dramatically. Science works! #fitness #recovery',
    tags: ['cardio', 'fitness', 'recovery'],
    authorId: 'mock_user_009',
    likesCount: 29,
    commentsCount: 7,
    createdAt: subDays(now, 5).toISOString(),
  }),

  // ── WORKOUT POSTS ──────────────────────────────────────────────────────────
  makePost({
    id: 'mock_post_w01',
    type: 'workout',
    text: '#pushday done! Really focused on mind-muscle connection today. The pump was unreal. #gains #gymlife',
    tags: ['pushday', 'gains', 'gymlife'],
    authorId: MOCK_CURRENT_USER_ID,
    likesCount: 23,
    commentsCount: 4,
    createdAt: subHours(now, 4).toISOString(),
    workout: {
      title: 'Push Day - Chest & Shoulders',
      workoutType: 'strength',
      duration: 65,
      caloriesBurned: 420,
      exercises: [
        { name: 'Bench Press', sets: [{ reps: 10, weight: 185, unit: 'lbs' }, { reps: 8, weight: 195, unit: 'lbs' }, { reps: 8, weight: 195, unit: 'lbs' }, { reps: 6, weight: 205, unit: 'lbs' }] },
        { name: 'Overhead Press', sets: [{ reps: 10, weight: 95, unit: 'lbs' }, { reps: 8, weight: 105, unit: 'lbs' }, { reps: 8, weight: 105, unit: 'lbs' }] },
        { name: 'Incline Dumbbell Press', sets: [{ reps: 12, weight: 60, unit: 'lbs' }, { reps: 10, weight: 65, unit: 'lbs' }, { reps: 10, weight: 65, unit: 'lbs' }] },
        { name: 'Lateral Raises', sets: [{ reps: 15, weight: 20, unit: 'lbs' }, { reps: 15, weight: 20, unit: 'lbs' }, { reps: 12, weight: 25, unit: 'lbs' }] },
        { name: 'Tricep Pushdowns', sets: [{ reps: 15, weight: 50, unit: 'lbs' }, { reps: 12, weight: 55, unit: 'lbs' }, { reps: 12, weight: 55, unit: 'lbs' }] },
      ],
    },
  }),
  makePost({
    id: 'mock_post_w02',
    type: 'workout',
    text: '#legday is the best day. Don\'t skip it! #noexcuses',
    tags: ['legday', 'noexcuses'],
    authorId: 'mock_user_003',
    likesCount: 41,
    commentsCount: 6,
    createdAt: subHours(now, 8).toISOString(),
    workout: {
      title: 'Leg Day - Quads & Glutes',
      workoutType: 'strength',
      duration: 75,
      caloriesBurned: 520,
      exercises: [
        { name: 'Back Squat', sets: [{ reps: 5, weight: 225, unit: 'lbs' }, { reps: 5, weight: 245, unit: 'lbs' }, { reps: 5, weight: 265, unit: 'lbs' }, { reps: 5, weight: 275, unit: 'lbs' }, { reps: 5, weight: 225, unit: 'lbs' }] },
        { name: 'Romanian Deadlift', sets: [{ reps: 10, weight: 185, unit: 'lbs' }, { reps: 10, weight: 185, unit: 'lbs' }, { reps: 8, weight: 195, unit: 'lbs' }] },
        { name: 'Leg Press', sets: [{ reps: 12, weight: 360, unit: 'lbs' }, { reps: 10, weight: 400, unit: 'lbs' }, { reps: 10, weight: 400, unit: 'lbs' }] },
        { name: 'Walking Lunges', sets: [{ reps: 12, weight: 50, unit: 'lbs' }, { reps: 12, weight: 50, unit: 'lbs' }] },
        { name: 'Calf Raises', sets: [{ reps: 20, weight: 135, unit: 'lbs' }, { reps: 20, weight: 135, unit: 'lbs' }, { reps: 15, weight: 155, unit: 'lbs' }] },
      ],
    },
  }),
  makePost({
    id: 'mock_post_w03',
    type: 'workout',
    text: '#hiit session at the park. 30 minutes of pure intensity. Feeling alive! #fitness #cardio',
    tags: ['hiit', 'fitness', 'cardio'],
    authorId: 'mock_user_006',
    likesCount: 19,
    commentsCount: 3,
    createdAt: subDays(now, 1).toISOString(),
    workout: {
      title: 'Park HIIT Circuit',
      workoutType: 'hiit',
      duration: 32,
      caloriesBurned: 380,
      exercises: [
        { name: 'Burpees', sets: [{ reps: 15, weight: 0, unit: 'lbs' }, { reps: 15, weight: 0, unit: 'lbs' }, { reps: 12, weight: 0, unit: 'lbs' }] },
        { name: 'Box Jumps', sets: [{ reps: 12, weight: 0, unit: 'lbs' }, { reps: 12, weight: 0, unit: 'lbs' }, { reps: 10, weight: 0, unit: 'lbs' }] },
        { name: 'Mountain Climbers', sets: [{ reps: 30, weight: 0, unit: 'lbs' }, { reps: 30, weight: 0, unit: 'lbs' }] },
        { name: 'Jump Squats', sets: [{ reps: 15, weight: 0, unit: 'lbs' }, { reps: 15, weight: 0, unit: 'lbs' }] },
        { name: 'Sprint Intervals', sets: [{ reps: 6, weight: 0, unit: 'lbs' }] },
      ],
    },
  }),
  makePost({
    id: 'mock_post_w04',
    type: 'workout',
    text: 'Morning flow to start the day right. Namaste. #yoga #morningroutine',
    tags: ['yoga', 'morningroutine'],
    authorId: 'mock_user_002',
    likesCount: 35,
    commentsCount: 5,
    createdAt: subDays(now, 2).toISOString(),
    workout: {
      title: 'Morning Yoga Flow',
      workoutType: 'flexibility',
      duration: 45,
      caloriesBurned: 180,
      exercises: [
        { name: 'Sun Salutation A', sets: [{ reps: 5, weight: 0, unit: 'lbs' }] },
        { name: 'Sun Salutation B', sets: [{ reps: 5, weight: 0, unit: 'lbs' }] },
        { name: 'Warrior Flow', sets: [{ reps: 3, weight: 0, unit: 'lbs' }] },
        { name: 'Hip Openers', sets: [{ reps: 1, weight: 0, unit: 'lbs' }] },
        { name: 'Cool Down Stretches', sets: [{ reps: 1, weight: 0, unit: 'lbs' }] },
      ],
    },
  }),
  makePost({
    id: 'mock_post_w05',
    type: 'workout',
    text: '#pullday with a focus on back width. Lat pulldowns were feeling great today. #gains @alexrivera',
    tags: ['pullday', 'gains'],
    mentions: ['mock_user_001'],
    authorId: 'mock_user_005',
    likesCount: 28,
    commentsCount: 4,
    createdAt: subDays(now, 3).toISOString(),
    workout: {
      title: 'Pull Day - Back & Biceps',
      workoutType: 'strength',
      duration: 58,
      caloriesBurned: 390,
      exercises: [
        { name: 'Deadlift', sets: [{ reps: 5, weight: 315, unit: 'lbs' }, { reps: 5, weight: 335, unit: 'lbs' }, { reps: 3, weight: 365, unit: 'lbs' }] },
        { name: 'Lat Pulldown', sets: [{ reps: 12, weight: 140, unit: 'lbs' }, { reps: 10, weight: 150, unit: 'lbs' }, { reps: 10, weight: 150, unit: 'lbs' }] },
        { name: 'Barbell Row', sets: [{ reps: 10, weight: 155, unit: 'lbs' }, { reps: 8, weight: 165, unit: 'lbs' }, { reps: 8, weight: 165, unit: 'lbs' }] },
        { name: 'Barbell Curl', sets: [{ reps: 12, weight: 65, unit: 'lbs' }, { reps: 10, weight: 75, unit: 'lbs' }, { reps: 10, weight: 75, unit: 'lbs' }] },
        { name: 'Face Pulls', sets: [{ reps: 15, weight: 40, unit: 'lbs' }, { reps: 15, weight: 40, unit: 'lbs' }] },
      ],
    },
  }),
  makePost({
    id: 'mock_post_w06',
    type: 'workout',
    text: 'Steady state #cardio for the win. 45 minutes on the rower followed by a cool-down walk. #fitness',
    tags: ['cardio', 'fitness'],
    authorId: 'mock_user_009',
    likesCount: 14,
    commentsCount: 2,
    createdAt: subDays(now, 4).toISOString(),
    workout: {
      title: 'Zone 2 Cardio Session',
      workoutType: 'cardio',
      duration: 55,
      caloriesBurned: 450,
      exercises: [
        { name: 'Rowing Machine', sets: [{ reps: 1, weight: 0, unit: 'lbs' }] },
        { name: 'Walking Cool-Down', sets: [{ reps: 1, weight: 0, unit: 'lbs' }] },
      ],
    },
  }),
  makePost({
    id: 'mock_post_w07',
    type: 'workout',
    text: 'Full body blast! Short on time so I did a circuit. In and out in 40 minutes. #calisthenics #noexcuses',
    tags: ['calisthenics', 'noexcuses'],
    authorId: 'mock_user_007',
    likesCount: 16,
    commentsCount: 3,
    createdAt: subDays(now, 6).toISOString(),
    workout: {
      title: 'Full Body Circuit',
      workoutType: 'strength',
      duration: 40,
      caloriesBurned: 350,
      exercises: [
        { name: 'Pull-Ups', sets: [{ reps: 12, weight: 0, unit: 'lbs' }, { reps: 10, weight: 0, unit: 'lbs' }, { reps: 8, weight: 0, unit: 'lbs' }] },
        { name: 'Dips', sets: [{ reps: 15, weight: 0, unit: 'lbs' }, { reps: 12, weight: 0, unit: 'lbs' }, { reps: 10, weight: 0, unit: 'lbs' }] },
        { name: 'Pistol Squats', sets: [{ reps: 8, weight: 0, unit: 'lbs' }, { reps: 8, weight: 0, unit: 'lbs' }] },
        { name: 'Handstand Push-Ups', sets: [{ reps: 5, weight: 0, unit: 'lbs' }, { reps: 5, weight: 0, unit: 'lbs' }] },
        { name: 'L-Sit Hold', sets: [{ reps: 3, weight: 0, unit: 'lbs' }] },
      ],
    },
  }),

  // ── MEAL POSTS ─────────────────────────────────────────────────────────────
  makePost({
    id: 'mock_post_m01',
    type: 'meal',
    text: 'Post-workout fuel! This shake is the perfect #recovery meal. #protein #nutrition',
    tags: ['recovery', 'protein', 'nutrition'],
    authorId: MOCK_CURRENT_USER_ID,
    likesCount: 15,
    commentsCount: 3,
    createdAt: subHours(now, 3).toISOString(),
    meal: {
      title: 'Protein Recovery Shake',
      mealType: 'snack',
      calories: 520,
      macros: { protein: 45, carbs: 62, fat: 12, fiber: 6 },
      ingredients: ['Whey protein (2 scoops)', 'Banana', 'Oats (1/2 cup)', 'Almond milk', 'Peanut butter (1 tbsp)', 'Ice'],
    },
  }),
  makePost({
    id: 'mock_post_m02',
    type: 'meal',
    text: '#mealprep Sunday! Made enough chicken and rice for the whole week. #nutrition #protein',
    tags: ['mealprep', 'nutrition', 'protein'],
    authorId: 'mock_user_005',
    likesCount: 38,
    commentsCount: 11,
    createdAt: subDays(now, 1).toISOString(),
    meal: {
      title: 'Grilled Chicken & Brown Rice Bowl',
      mealType: 'lunch',
      calories: 580,
      macros: { protein: 48, carbs: 65, fat: 14, fiber: 5 },
      ingredients: ['Grilled chicken breast (6oz)', 'Brown rice (1 cup cooked)', 'Steamed broccoli', 'Teriyaki sauce (1 tbsp)', 'Sesame seeds'],
    },
  }),
  makePost({
    id: 'mock_post_m03',
    type: 'meal',
    text: 'Greek yogurt parfait to start the morning right!',
    authorId: 'mock_user_004',
    likesCount: 21,
    commentsCount: 4,
    createdAt: subDays(now, 2).toISOString(),
    meal: {
      title: 'Greek Yogurt Parfait',
      mealType: 'breakfast',
      calories: 380,
      macros: { protein: 28, carbs: 45, fat: 10, fiber: 4 },
      ingredients: ['Greek yogurt (1 cup)', 'Mixed berries', 'Granola (1/4 cup)', 'Honey drizzle', 'Chia seeds (1 tsp)'],
    },
  }),
  makePost({
    id: 'mock_post_m04',
    type: 'meal',
    text: 'Dinner tonight was chef\'s kiss. Salmon is just the best protein source.',
    authorId: 'mock_user_002',
    likesCount: 44,
    commentsCount: 8,
    createdAt: subDays(now, 3).toISOString(),
    meal: {
      title: 'Herb-Crusted Salmon with Sweet Potato',
      mealType: 'dinner',
      calories: 640,
      macros: { protein: 42, carbs: 52, fat: 28, fiber: 7 },
      ingredients: ['Atlantic salmon fillet (6oz)', 'Sweet potato (medium)', 'Asparagus (6 spears)', 'Lemon', 'Garlic', 'Fresh herbs', 'Olive oil (1 tbsp)'],
    },
  }),
  makePost({
    id: 'mock_post_m05',
    type: 'meal',
    text: 'Quick and easy high-protein snack between meetings.',
    authorId: 'mock_user_010',
    likesCount: 12,
    commentsCount: 2,
    createdAt: subDays(now, 4).toISOString(),
    meal: {
      title: 'Turkey & Avocado Rice Cakes',
      mealType: 'snack',
      calories: 290,
      macros: { protein: 22, carbs: 28, fat: 12, fiber: 3 },
      ingredients: ['Rice cakes (2)', 'Turkey breast slices (3oz)', 'Avocado (1/4)', 'Everything bagel seasoning', 'Cherry tomatoes'],
    },
  }),
  makePost({
    id: 'mock_post_m06',
    type: 'meal',
    text: 'Overnight oats are a game changer for busy mornings. 5 min prep the night before! #mealprep #morningroutine',
    tags: ['mealprep', 'morningroutine'],
    authorId: 'mock_user_008',
    likesCount: 26,
    commentsCount: 6,
    createdAt: subDays(now, 6).toISOString(),
    meal: {
      title: 'Chocolate Peanut Butter Overnight Oats',
      mealType: 'breakfast',
      calories: 450,
      macros: { protein: 22, carbs: 58, fat: 16, fiber: 8 },
      ingredients: ['Rolled oats (1/2 cup)', 'Chocolate protein powder (1 scoop)', 'Peanut butter (1 tbsp)', 'Almond milk (3/4 cup)', 'Banana slices', 'Dark chocolate chips'],
    },
  }),

  // ── PROGRESS POSTS ─────────────────────────────────────────────────────────
  makePost({
    id: 'mock_post_p01',
    type: 'progress',
    text: 'Three month #transformation update! Down 13lbs and feeling stronger than ever. Consistency is king. #fitness #gains @sarahchen',
    tags: ['transformation', 'fitness', 'gains'],
    mentions: ['mock_user_002'],
    authorId: MOCK_CURRENT_USER_ID,
    likesCount: 52,
    commentsCount: 15,
    createdAt: subDays(now, 1).toISOString(),
    progress: {
      beforeImageUrl: 'https://placehold.co/400x500/e8e8e8/666?text=Before',
      afterImageUrl: 'https://placehold.co/400x500/d4edda/155724?text=After',
      milestone: 'First 10lbs lost!',
      stats: {
        startWeight: 195,
        currentWeight: 182,
        startBodyFat: 22,
        currentBodyFat: 18,
      },
      duration: '3 months',
    },
  }),
  makePost({
    id: 'mock_post_p02',
    type: 'progress',
    text: '6 months of dedicated training. The back gains are real! Posing practice is next on the agenda.',
    authorId: 'mock_user_005',
    likesCount: 67,
    commentsCount: 13,
    createdAt: subDays(now, 5).toISOString(),
    progress: {
      beforeImageUrl: 'https://placehold.co/400x500/e8e8e8/666?text=Month+1',
      afterImageUrl: 'https://placehold.co/400x500/d4edda/155724?text=Month+6',
      milestone: 'Stage-ready conditioning!',
      stats: {
        startWeight: 180,
        currentWeight: 175,
        startBodyFat: 16,
        currentBodyFat: 10,
      },
      duration: '6 months',
    },
  }),
  makePost({
    id: 'mock_post_p03',
    type: 'progress',
    text: 'One year of #calisthenics. Started with zero pull-ups, now I can do 20 strict. The journey is worth it. #transformation #fitness',
    tags: ['calisthenics', 'transformation', 'fitness'],
    authorId: 'mock_user_007',
    likesCount: 89,
    commentsCount: 12,
    createdAt: subDays(now, 8).toISOString(),
    progress: {
      beforeImageUrl: 'https://placehold.co/400x500/e8e8e8/666?text=Day+1',
      afterImageUrl: 'https://placehold.co/400x500/d4edda/155724?text=Year+1',
      milestone: '20 strict pull-ups achieved!',
      stats: {
        startWeight: 170,
        currentWeight: 165,
        startBodyFat: 20,
        currentBodyFat: 14,
      },
      duration: '12 months',
    },
  }),
  makePost({
    id: 'mock_post_p04',
    type: 'progress',
    text: 'Postpartum fitness journey - 4 months in. Taking it slow and being kind to myself. Strong is beautiful.',
    authorId: 'mock_user_004',
    likesCount: 73,
    commentsCount: 11,
    createdAt: subDays(now, 10).toISOString(),
    progress: {
      beforeImageUrl: 'https://placehold.co/400x500/e8e8e8/666?text=Month+0',
      afterImageUrl: 'https://placehold.co/400x500/d4edda/155724?text=Month+4',
      milestone: 'Back in the gym!',
      stats: {
        startWeight: 160,
        currentWeight: 148,
        startBodyFat: null,
        currentBodyFat: null,
      },
      duration: '4 months',
    },
  }),
];
